import { useQuery } from '@tanstack/react-query';
import { relireUneScene } from '@groupe-trousseau/shared/api-client';
import { deballer } from '../../shared/api/client';
import { CLES } from '../../shared/donnees/cles';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA RELECTURE D'UN COURS PASSÉ — `CDC-4` F-47.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **LA LECTURE D'UNE SCÈNE LAISSE UNE LIGNE DE TRACE, ET C'EST LA SEULE DU DRIVE ET DE
 * L'HISTOIRE QUI EN LAISSE UNE.** « Parcourir son historique ne laisse aucune ligne.
 * **Ouvrir la scène d'un cours en laisse une**, quel que soit le lecteur » (F-46, DD-12).
 *
 * **Conséquence directe sur ce hook, et elle n'est pas théorique** : chaque appel écrit en
 * base. Le cache de TanStack Query n'est donc pas un confort ici, **c'est ce qui évite
 * d'écrire une ligne d'audit à chaque rendu de React**. Et il ne se rafraîchit pas au
 * retour sur l'onglet : une scène figée ne change plus, par définition — « elle l'est dès
 * la fin du cours ou à l'ouverture du tableau suivant, et **personne ne l'écrit plus** ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE LA RELECTURE PEUT REFUSER, ET LES DEUX REFUS SE DISENT DIFFÉREMMENT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * - **`SCENE_EFFACEE`** : la durée de relecture est passée. L'historique **ne propose déjà
 *   plus le geste** dans ce cas — il rend `plus_consultable` —, donc ce refus n'arrive que
 *   sur une adresse tapée à la main ou sur un onglet resté ouvert trop longtemps.
 * - **`TABLEAU_SANS_TRACE`** : « la relecture est refusée sur un tableau sans aucun tracé »
 *   (Q321), **y compris le dernier ouvert juste avant la fin**. C'est le cas courant en
 *   circulant : un cours peut finir sur un tableau neuf jamais écrit.
 */
export function useScene(coursId: string, rang: number) {
  return useQuery({
    queryKey: CLES.cours.tableau(coursId, rang),
    queryFn: () =>
      deballer(relireUneScene({ path: { id: coursId, rang } })),
    // **Une scène figée ne change plus**, donc rien ne justifie de la relire — et chaque
    // relecture écrit une ligne d'audit.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // **Aucun réessai automatique** : les deux refus possibles sont métier, pas réseau.
    // Réessayer trois fois un `TABLEAU_SANS_TRACE` écrirait trois lignes pour un refus
    // qui ne changera pas.
    retry: false,
  });
}
